<script lang="ts">
	import ProductCardCartOptions from "@components/ProductCardCartOptions.svelte";
	import ProductCardMedia from "@components/ProductCardMedia.svelte";
	import ProductCardMediaFallback from "@components/ProductCardMediaFallback.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import type { OrderProduct } from "@core/schema/OrderProduct.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import { getProductDetailPath } from "@core/schema/paths/getProductDetailPath.js";
	import { getProductHeadingWithAttributes } from "@core/schema/utils/getProductHeadingWithAttributes.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const {
		orderProduct,
	}: {
		orderProduct: OrderProduct;
	} = $props();

	const { tenant, subcategories } = getGlobalTemplateProps();

	const subcategory = $derived(subcategories.find(({ id }) => id === orderProduct.info.subcategoryId));
	const description = $derived.by(() => {
		let text = subcategory?.name[tenant.locale] ?? "";
		if (orderProduct.info.variantType !== ProductVariantType.None) {
			text += ` • ${orderProduct.info.variant.label[tenant.locale]}`;
		}
		text += ` • ${orderProduct.quantity} ks`;
		return text;
	});
	const productInCart = $derived(Cart.findProduct(orderProduct));
	const mediaSrc = $derived(productInCart && getProductPrimaryMedia(productInCart, orderProduct.info.attributes));

	const link = $derived(
		productInCart &&
			subcategory &&
			getProductDetailPath(
				tenant.locale,
				productInCart,
				subcategory,
				orderProduct.info.variant,
				orderProduct.info.attributes,
			),
	);

	const heading = $derived(getProductHeadingWithAttributes(tenant.locale, orderProduct.info));

	const max = $derived(
		!productInCart
			? 0
			: getProductStockAmount(productInCart.stock, orderProduct.info.variant, orderProduct.info.attributes),
	);
</script>

<div class="grid grid-cols-[9.875rem_1fr_1fr] p-2 items-center border-fcrlightgray-300 border rounded-3xl gap-6">
	<a href={link} class="grow-0 shrink-0 row-span-2">
		<div class="w-39.5 h-33 bg-fcrlightgray-400 relative rounded-[1.125rem] object-contain overflow-hidden">
			{#if mediaSrc}
				<ProductCardMedia {mediaSrc} alt={heading} />
			{:else}
				<ProductCardMediaFallback />
			{/if}
		</div>
	</a>

	<div class="flex flex-col gap-1 col-span-2">
		<a
			href={link}
			class="font-antipol transition-colors hover:text-fcrbrown underline text-4.5 font-medium text-fcrdarkgray break-words"
		>
			<!-- Formatted component is not used because it forces product name not to break on line on small screens -->
			{heading}
		</a>
		<div class="font-medium text-fcrgray">
			<Formatted text={description} />
		</div>
	</div>

	<div class="font-antipol text-fcrdarkgray col-span-2 sm:col-span-1 lg:col-span-2 2xl:col-span-1">
		{#if Cart.currency}
			<Formatted text={formatPrice(orderProduct.info.price.withTax, Cart.currency, tenant.locale, true)} />
		{/if}
	</div>

	{#if productInCart}
		<div class="flex justify-end col-span-3 sm:col-span-1 lg:col-span-3 2xl:col-span-1">
			<ProductCardCartOptions
				showToasts={false}
				product={productInCart}
				attributes={orderProduct.info.attributes}
				{max}
			/>
		</div>
	{/if}
</div>
